// The issues, last changed first, with how far each one reaches (its evidence, the users and organizations behind it)
// and a form to open one by hand. Filters live in the URL.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage, useAsync } from '../../hooks/useAsync';
import { int, useQueryParams } from '../../hooks/useQueryParams';
import { dateTime } from '../../services/format';
import { Alert, Badge, Button, Card, Cell, Field, Filters, Input, PageTitle, Pager, Row, Select, Table, Textarea } from '../../components/ui';
import type { IssueLinkKind, IssueSeverity, IssuesQuery } from '../../types/api';
import { ISSUE_SEVERITIES, IssueSeverityBadge, IssueStatusBadge } from './common';

const PAGE_SIZE = 50;
const KINDS: IssueLinkKind[] = ['conversation', 'report', 'review', 'signal'];

function NewIssue({ onCancel }: { onCancel: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<IssueSeverity>('medium');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const issue = await api.createIssue({ title: title.trim(), description: description.trim() || null, severity });
      navigate(`/issues/${issue.id}`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Card title={t('issues.new')} className="mb-3">
      <div className="flex flex-col gap-2">
        {error && <Alert>{error}</Alert>}
        <Field label={t('issues.titleLabel')}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('issues.titlePlaceholder')} data-testid="new-issue-title" />
        </Field>
        <Field label={t('issues.descriptionLabel')}>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} />
        </Field>
        <Field label={t('issues.severityLabel')} className="w-40">
          <Select value={severity} onChange={(e) => setSeverity(e.target.value as IssueSeverity)}>
            {ISSUE_SEVERITIES.map((s) => (
              <option key={s} value={s}>{t(`issues.severity.${s}`)}</option>
            ))}
          </Select>
        </Field>
        <div className="flex gap-2">
          <Button variant="primary" busy={busy} disabled={!title.trim()} onClick={() => void create()} data-testid="create-issue">
            {t('issues.create')}
          </Button>
          <Button variant="ghost" onClick={onCancel}>{t('common.cancel')}</Button>
        </div>
      </div>
    </Card>
  );
}

export function IssuesList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { get, page, set, reset, setPage } = useQueryParams();
  const [creating, setCreating] = useState(false);
  const status = get('status');
  const query: IssuesQuery = {
    status: status === 'closed' || status === 'all' ? status : 'open',
    severity: (ISSUE_SEVERITIES as string[]).includes(get('severity')) ? (get('severity') as IssueSeverity) : undefined,
    owner_id: int(get('owner_id')),
    label: get('label') || undefined,
    q: get('q') || undefined,
  };
  const key = JSON.stringify(query);
  const { data, error, loading } = useAsync(() => api.issues({ ...query, page, page_size: PAGE_SIZE }), [key, page]);

  return (
    <div>
      <PageTitle title={t('issues.title')} subtitle={data ? t('common.count', { count: data.total }) : undefined}
                 actions={<Button variant="primary" onClick={() => setCreating(true)} data-testid="new-issue">{t('issues.new')}</Button>} />
      {creating && <NewIssue onCancel={() => setCreating(false)} />}
      <Filters onReset={reset}>
        <Field label={t('common.search')} className="w-56">
          <Input value={get('q')} onChange={(e) => set({ q: e.target.value })} placeholder={t('issues.searchHint')} />
        </Field>
        <Field label={t('issues.statusLabel')}>
          <Select value={query.status} onChange={(e) => set({ status: e.target.value === 'open' ? undefined : e.target.value })} data-testid="status-filter">
            <option value="open">{t('issues.statusFilter.open')}</option>
            <option value="closed">{t('issues.statusFilter.closed')}</option>
            <option value="all">{t('issues.statusFilter.all')}</option>
          </Select>
        </Field>
        <Field label={t('issues.severityLabel')}>
          <Select value={get('severity')} onChange={(e) => set({ severity: e.target.value })}>
            <option value="">{t('common.any')}</option>
            {ISSUE_SEVERITIES.map((s) => (
              <option key={s} value={s}>{t(`issues.severity.${s}`)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('issues.labelsLabel')} className="w-40">
          <Input value={get('label')} onChange={(e) => set({ label: e.target.value })} />
        </Field>
      </Filters>
      {error && <Alert>{error}</Alert>}
      <Table
        head={['#', t('issues.titleLabel'), t('issues.statusLabel'), t('issues.severityLabel'), t('issues.evidence'), t('issues.affected'), t('issues.owner'), t('issues.lastSeen')]}
        empty={loading ? t('common.loading') : t('common.empty')}
      >
        {(data?.items ?? []).map((i) => (
          <Row key={i.id} onClick={() => navigate(`/issues/${i.id}`)}>
            <Cell mono>{i.id}</Cell>
            <Cell className="max-w-md">
              <div className="truncate text-ink" title={i.title}>{i.title}</div>
              {!!i.labels.length && (
                <div className="mt-0.5 flex flex-wrap gap-1">
                  {i.labels.map((l) => <Badge key={l}>{l}</Badge>)}
                </div>
              )}
            </Cell>
            <Cell><IssueStatusBadge status={i.status} /></Cell>
            <Cell><IssueSeverityBadge severity={i.severity} /></Cell>
            <Cell className="whitespace-nowrap text-[12px] text-muted">
              {KINDS.filter((k) => i.links[k]).map((k) => t(`issues.kindCount.${k}`, { count: i.links[k] })).join(' · ') || '—'}
            </Cell>
            <Cell mono className="whitespace-nowrap">{t('issues.affectedValue', { users: i.users, orgs: i.organizations })}</Cell>
            <Cell mono>{i.owner_email ?? '—'}</Cell>
            <Cell mono className="whitespace-nowrap">{dateTime(i.last_seen ?? i.updated_at, i18n.language)}</Cell>
          </Row>
        ))}
      </Table>
      {data && <Pager page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />}
    </div>
  );
}
